import { AppLayout } from "@heroui-pro/react/app-layout";
import { Navbar } from "@heroui-pro/react/navbar";
import { Sidebar } from "@heroui-pro/react/sidebar";
import { Avatar, Button, Dropdown } from "@heroui/react";
import { ArrowRightFromSquare, Bell, Gear, Moon, Sun } from "@gravity-ui/icons";
import { useTheme } from "@/components/providers/theme-provider";
import { AppLink as Link } from "@/components/ui/app-link";
import type { ReactNode } from "react";

import { toStaticExportHref } from "../../config/normalize-application-pathname";
import { useAppI18n } from "../../i18n/context";
import { BrandMark } from "../brand/brand-mark";
import { LanguageSwitcher } from "../i18n/language-switcher";

export type DashboardNavbarProps = {
  isAdmin?: boolean;
  isSigningOut?: boolean;
  signOutError?: string | null;
  profileDisplayName?: string;
  profileImage?: string | null;
  accountRoleLabel?: string | null;
  failedAvatar?: string | null;
  onAvatarError?: () => void;
  onSignOut?: () => void;
  onToggleTheme?: () => void;
  resolvedTheme?: string;
};

/** 顶栏：侧栏开关、品牌、语言 / 主题 / 账户 / 退出。 */
export function DashboardNavbar({
  isAdmin = false,
  isSigningOut = false,
  signOutError = null,
  profileDisplayName,
  profileImage,
  accountRoleLabel,
  failedAvatar = null,
  onAvatarError,
  onSignOut,
  onToggleTheme,
  resolvedTheme,
}: DashboardNavbarProps) {
  const { t } = useAppI18n();
  const themeApi = useTheme();
  const theme = resolvedTheme ?? themeApi.resolvedTheme;
  const setTheme = onToggleTheme
    ? undefined
    : themeApi.setTheme;

  return (
    <Navbar className="application-navbar" maxWidth="full">
      <Navbar.Header>
        <div className="application-navbar__leading">
          <AppLayout.MenuToggle aria-label={t("shell.openNavigation")} />
          <Sidebar.Trigger aria-label={t("shell.toggleSidebar")} />
          <BrandMark showTagline />
        </div>
        <Navbar.Spacer />
        <Navbar.Content className="application-navbar__actions">
          <LanguageSwitcher />
          {signOutError ? (
            <p className="text-danger max-w-48 truncate text-xs" role="alert">
              {signOutError}
            </p>
          ) : null}
          {!isAdmin ? (
            <Button
              aria-label={t("shell.notifications")}
              isIconOnly
              size="sm"
              variant="ghost"
            >
              <Bell width={17} height={17} />
            </Button>
          ) : null}
          <Button
            aria-label={t("shell.toggleTheme")}
            isIconOnly
            onPress={() => {
              if (onToggleTheme) {
                onToggleTheme();
                return;
              }
              setTheme?.(theme === "dark" ? "light" : "dark");
            }}
            size="sm"
            variant="ghost"
          >
            {theme === "dark" ? <Sun width={17} height={17} /> : <Moon width={17} height={17} />}
          </Button>
          <Dropdown>
            <Button
              aria-label={t("shell.accountMenu")}
              isIconOnly
              size="sm"
              variant="ghost"
            >
              <Avatar
                aria-label={profileDisplayName}
                key={
                  profileImage && failedAvatar !== profileImage
                    ? profileImage
                    : "avatar-fallback"
                }
                size="sm"
              >
                {profileImage && failedAvatar !== profileImage ? (
                  <Avatar.Image
                    alt={profileDisplayName}
                    onError={onAvatarError}
                    src={profileImage}
                  />
                ) : null}
                <Avatar.Fallback>
                  {profileDisplayName?.slice(0, 1).toUpperCase()}
                </Avatar.Fallback>
              </Avatar>
            </Button>
            <Dropdown.Popover>
              <div className="grid gap-1 px-3 py-2">
                {profileDisplayName ? (
                  <p className="text-sm font-medium">{profileDisplayName}</p>
                ) : null}
                {accountRoleLabel ? (
                  <p className="text-muted text-xs">{accountRoleLabel}</p>
                ) : null}
              </div>
              <Dropdown.Menu>
                <Dropdown.Item
                  id="settings"
                  textValue={t("shell.accountSettings")}
                >
                  <Gear width={16} height={16} />
                  <Link
                    href={toStaticExportHref("/settings")}
                    prefetch={false}
                  >
                    {t("shell.accountSettings")}
                  </Link>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown.Popover>
          </Dropdown>
          <Button
            aria-label={t("shell.signOut")}
            isIconOnly
            isPending={isSigningOut}
            onPress={() => onSignOut?.()}
            size="sm"
            variant="ghost"
          >
            <ArrowRightFromSquare width={16} height={16} />
          </Button>
        </Navbar.Content>
      </Navbar.Header>
    </Navbar>
  );
}

/** 供页面操作插槽等扩展；当前顶栏无额外 children。 */
export function DashboardNavbarAdjacent({
  children,
}: {
  children?: ReactNode;
}) {
  return children ? <>{children}</> : null;
}
